import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  EventEmitter,
  HostBinding,
  Input,
  OnChanges,
  OnInit,
  Output,
  SimpleChanges
} from '@angular/core';

import { valueAccessorFactory } from '../../helpers';
import { BaseInputDirective } from '../base-input.directive';
import { formControlFactory } from '../form-control.provider';
import { TextareaSize } from './textarea-size';

@Component({
  selector: 'pcs-textarea',
  templateUrl: './textarea.component.html',
  styleUrls: ['./textarea.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  providers: [
    valueAccessorFactory(TextareaComponent),
    formControlFactory(TextareaComponent)
  ]
})
export class TextareaComponent extends BaseInputDirective implements OnInit, OnChanges {
  @Input() value = '';
  @Input() placeholder = '';
  @Input() helperText: string;
  @Input() tabIndex;
  @Input() size = TextareaSize.Default;
  @Input() rows: number;
  @Input()
  @HostBinding('class.row') horizontal = false;

  @Output() changed = new EventEmitter();
  @Output() blured = new EventEmitter();

  labelClasses = null;
  inputClasses = null;

  @HostBinding('class.mb-half')
  private get editAndSmall() {
    return !this.readonly && this.isSmall();
  }

  @HostBinding('class.mb-0')
  private get readonlyAndSmall() {
    return this.readonly && this.isSmall();
  }

  constructor(private cdr: ChangeDetectorRef) {
    super();
  }

  ngOnInit(): void {
    this.labelClasses = this.calculateLabelClasses();
    this.inputClasses = this.calculateInputClasses();
  }

  ngOnChanges({ size, horizontal, readonly }: SimpleChanges): void {
    if (size || horizontal || readonly) {
      this.labelClasses = this.calculateLabelClasses();
      this.inputClasses = this.calculateInputClasses();
    }
  }

  onInput(event) {
    this.value = event.target.value;
    this.propagateChange(this.value);
    this.changed.emit(this.value);
  }

  onBlur(event) {
    this.value = event.target.value;
    this.propagateTouched(event);
    this.blured.emit(event);
  }

  writeValue(value: any): void {
    this.value = value;
    this.cdr.markForCheck();
  }

  private calculateLabelClasses() {
    return {
      'col-sm-2 col-form-label': this.horizontal && !this.isSmall(),
      'col-form-label-sm': this.isSmall()
    };
  }

  private calculateInputClasses() {
    return {
      '': this.isDefault(),
      'form-control': !this.readonly,
      'form-control-sm': this.isSmall(),
      'form-control-lg': this.isLarge()
    };
  }

  private isDefault() {
    return this.size === TextareaSize.Default;
  }

  private isSmall() {
    return this.size === TextareaSize.Small;
  }

  private isLarge() {
    return this.size === TextareaSize.Large;
  }
}
